import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts'

type OidcEnvironment = Record<string, string | undefined>

type ConfiguredOidc = {
  configured: true
  authority: string
  clientId: string
  redirectUri: string
  postLogoutRedirectUri: string
  scope: string
  globalLogoutUri?: string
}

export type OidcConfig = ConfiguredOidc | { configured: false }

const pageOrigin = (): string | undefined =>
  typeof window !== 'undefined' ? window.location.origin : undefined

/**
 * Test environments serve a mock identity provider through the studio's own origin, at whatever
 * host the preview gets: such builds configure paths ("/application/o/…") resolved against the page.
 */
const resolveUrl = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim()
  const origin = pageOrigin()
  return trimmed?.startsWith('/') && origin ? new URL(trimmed, origin).href : trimmed
}

const validAbsoluteUrl = (value: string, requireIssuerSlash = false): boolean => {
  try {
    const url = new URL(value)
    const isLocal = ['localhost', '127.0.0.1'].includes(url.hostname)
    const sameOrigin = url.origin === pageOrigin()
    if (url.protocol !== 'https:' && !(isLocal && url.protocol === 'http:') && !sameOrigin) {
      return false
    }
    if (url.username || url.password || url.hash) return false
    return !requireIssuerSlash || url.pathname.endsWith('/')
  } catch {
    return false
  }
}

export const readOidcConfig = (env: OidcEnvironment): OidcConfig => {
  const authority = resolveUrl(env.VITE_AUTHENTIK_ISSUER)
  const clientId = env.VITE_AUTHENTIK_CLIENT_ID?.trim()
  const redirectUri = resolveUrl(env.VITE_AUTHENTIK_REDIRECT_URI)
  const postLogoutRedirectUri = resolveUrl(env.VITE_AUTHENTIK_POST_LOGOUT_REDIRECT_URI)
  if (
    !authority ||
    !clientId ||
    !redirectUri ||
    !postLogoutRedirectUri ||
    !validAbsoluteUrl(authority, true) ||
    !validAbsoluteUrl(redirectUri) ||
    !validAbsoluteUrl(postLogoutRedirectUri)
  ) {
    return { configured: false }
  }
  const globalLogoutUri = env.VITE_AUTHENTIK_GLOBAL_LOGOUT_URI?.trim()
  return {
    configured: true,
    authority,
    clientId,
    redirectUri,
    postLogoutRedirectUri,
    scope: env.VITE_AUTHENTIK_SCOPE?.trim() || 'openid profile email permissions',
    globalLogoutUri:
      globalLogoutUri && validAbsoluteUrl(globalLogoutUri) ? globalLogoutUri : undefined,
  }
}

const sensitiveKeys = [
  'code',
  'state',
  'error',
  'error_description',
  'session_state',
  'id_token_hint',
]

const RETURN_TO_KEY = 'lightscape.auth.returnTo'

export const getSafeReturnTo = (value: unknown, fallback = '/'): string => {
  const hasControlCharacter =
    typeof value === 'string' &&
    [...value].some((character) => {
      const code = character.charCodeAt(0)
      return code <= 31 || code === 127
    })
  if (
    typeof value !== 'string' ||
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.includes('\\') ||
    hasControlCharacter
  ) {
    return fallback
  }
  try {
    const origin = globalThis.location?.origin ?? 'http://localhost'
    const target = new URL(value, origin)
    if (target.origin !== origin || target.pathname === '/auth/callback') return fallback
    for (const key of sensitiveKeys) target.searchParams.delete(key)
    return `${target.pathname}${target.search}${target.hash}`
  } catch {
    return fallback
  }
}

export const storePendingReturnTo = (value: unknown, storage: Storage = window.sessionStorage) =>
  storage.setItem(RETURN_TO_KEY, getSafeReturnTo(value))

export const consumePendingReturnTo = (storage: Storage = window.sessionStorage): string => {
  const value = storage.getItem(RETURN_TO_KEY)
  storage.removeItem(RETURN_TO_KEY)
  return getSafeReturnTo(value)
}

export const oidcConfig = readOidcConfig(import.meta.env)

export const userManager =
  oidcConfig.configured && typeof window !== 'undefined'
    ? new UserManager({
        authority: oidcConfig.authority,
        client_id: oidcConfig.clientId,
        redirect_uri: oidcConfig.redirectUri,
        post_logout_redirect_uri: oidcConfig.postLogoutRedirectUri,
        response_type: 'code',
        scope: oidcConfig.scope,
        automaticSilentRenew: false,
        monitorSession: false,
        userStore: new WebStorageStateStore({ store: window.sessionStorage }),
        stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
      })
    : null

export const getAccessToken = async (): Promise<string | null> => {
  const user = await userManager?.getUser()
  return user && !user.expired ? user.access_token : null
}

export type OidcUser = User
